import { error } from "@sveltejs/kit";
import { b as SECRET_KEY, R as RESET_KEY, c as REFRESH_KEY } from "./private.js";
import bcrypt from "bcrypt";
import jwt from "jsonwebtoken";
import { Roles } from "@prisma/client";
import { p as prisma } from "./prisma.js";
import { m as maxAge } from "./utils2.js";
import { randomUUID } from "crypto";
let roleCache;
const auth = {
  /**
   * Sign payload
   * @param payload - user object or minimal payload
   * @param sessionId - optional session id to include in JWT
   */
  sign(payload, sessionId) {
    const id = payload.id;
    const roleId = payload.roleId;
    const tokenPayload = { id, roleId };
    if (sessionId) tokenPayload.sessionId = sessionId;
    return jwt.sign(tokenPayload, SECRET_KEY, {
      expiresIn: maxAge
    });
  },
  async generateSessionId() {
    return randomUUID();
  },
  /**
   * Save a hashed session token to db
   * @param userId
   * @param sessionId raw token
   */
  async saveSessionToken(userId, sessionId) {
    const hashed = await bcrypt.hash(sessionId, 10);
    await prisma.user.update({
      where: { id: userId },
      data: { sessionToken: hashed, sessionTokenAt: /* @__PURE__ */ new Date() }
    });
  },
  /**
   * Verify raw session token against DB
   */
  async verifySessionToken(sessionId, userId) {
    if (!sessionId) return false;
    try {
      const user = await prisma.user.findUnique({
        where: { id: userId },
        select: { sessionToken: true }
      });
      if (!user || !user.sessionToken) return false;
      return await bcrypt.compare(sessionId, user.sessionToken);
    } catch (e) {
      console.error("verifySessionToken error", e);
      return false;
    }
  },
  /**
   * Generate refresh token, hash it and save it to the database
   * @returns hashed refreshToken
   */
  async generateRefreshToken(user) {
    const maxAge2 = 60 * 60 * 24 * 7;
    const id = user.id;
    try {
      const refreshToken = jwt.sign({ id }, REFRESH_KEY, {
        expiresIn: maxAge2
      });
      const hashedToken = await this.hash(refreshToken);
      await prisma.user.update({
        where: { id },
        data: { refreshToken: hashedToken }
      });
      return refreshToken;
    } catch (e) {
      console.log("Error generating refresh token", e);
      return "";
    }
  },
  /**
   * Verify refresh token
   */
  async verifyRefreshToken(token, userId) {
    try {
      const user = await prisma.user.findUnique({
        where: { id: userId },
        select: { refreshToken: true }
      });
      if (!user || !user.refreshToken) {
        return false;
      }
      const isMatch = await bcrypt.compare(token, user.refreshToken);
      return isMatch;
    } catch (e) {
      console.log("Error verifying refresh token", e);
      return false;
    }
  },
  /**
   * Generate reset Password token
   */
  generateResetToken(id) {
    const maxAge2 = 60 * 60;
    return jwt.sign({ id }, RESET_KEY, { expiresIn: maxAge2 });
  },
  /**
   * Generate Email Verification token
   */
  generateEmailVerificationToken(email) {
    const maxAge2 = 60 * 60;
    return jwt.sign({ email }, SECRET_KEY, { expiresIn: maxAge2 });
  },
  /**
   * Compare Passwords to it's hash
   */
  async compare(password, hash) {
    return await bcrypt.compare(password, hash);
  },
  /**
   * Hash Password
   * @param  password The password to encrypt
   * @returns Encrypted Password
   */
  async hash(password) {
    const salt = await bcrypt.genSalt();
    return await bcrypt.hash(password, salt);
  },
  /**
   * Get roles from the database
   * @returns roles
   **/
  async getRoles() {
    if (!roleCache) {
      console.log("Querying db for roles");
      await prisma.role.findMany().then((roles) => {
        roleCache = roles;
      }).catch((err) => {
        console.log("Error getting roles", err);
        throw error(500, "Error getting roles");
      });
    }
    return roleCache;
  },
  /**
   * Check if user is an admin
   * @param user
   */
  async isAdmin(user) {
    const roles = await this.getRoles();
    const adminRole = roles.find((role) => role.name === Roles.ADMIN);
    if (adminRole && user?.roleId === adminRole.id) return true;
    return false;
  }
};
export {
  auth as default
};
